import { makeTree } from '../../classifier/tree';
import { ClassificationDoesNotExists, FeatureDoesNotExists } from '../exceptions';

/**
 * Set the active item from the mappedItems array.
 * @param {Object} state
 * @param {Object} item
 */
function setActiveMappedItem(state, item) {
  state.activeMappedItem = item
  setActiveMappedItemInput(state, item ? item.mapped : null)
}

/**
 * Set the value in the input of the active item.
 * @param {Object} state
 * @param {String} value
 */
function setActiveMappedItemInput(state, value) {
  if (value) value = value.toUpperCase().trim()
  state.activeMappedItemInput = value
}

/**
 * Set the brand to filter the values.
 * @param {Object} state
 * @param {String} brand
 */
function setBrand(state, brand) {
  if (!brand) {
    state.brand = null
    setActiveMappedItem(state, null)
  } else {
    state.brand = brand
  }
}

/**
 * Set the current active classification.
 * @param {Object} state
 * @param {Number} id the identifier of the classification to set as active.
 */
function setClassification(state, id) {
  const classification = state.classifications.find(c => c.id === id);
  if (id && !classification) throw new ClassificationDoesNotExists()
  state.classification = classification ? { ...classification } : null
  if (!state.classification) state.feature = null
}

/**
* Set the array of available classifications.
* @param {Object} state 
* @param {Object[]} classifications The classifications to set.
*/
function setClassifications(state, classifications) {
  state.classifications = makeTree(classifications)
}

/**
 * Set the active feature in the store with it's id, name and values.
 * It's highly recommended to not use this mutation directly, instead, please
 * use the `setFeature` action that request the values of the feature that
 * you want to set.
 * @param {Object} state
 * @param {Number} id the feature's id to set.
 */
function setFeature(state, id) {
  if (!id) {
    return state.feature = null
  }

  const feature = state.features.find(f => f.id === id)
  if (!feature) {
    throw new FeatureDoesNotExists();
  }

  state.feature = { ...feature }
}

/**
 * Set the features objects of the active classification.
 * @param {OBject} state
 * @param {Object[]} features
 */
function setFeatures(state, features) {
  if (!Array.isArray(features) || features.some(item => typeof item !== 'object')) {
    throw new Error('Please provide an array of objects')
  }

  if (features.some(item => !item.id || !item.name)) {
    throw new Error('Please provide objects with "id" and "name')
  }

  state.features = features
}

/**
 * Set the value of the flag that indicates that the app is loading the classifications.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingClassifications(state, value) {
  state.loadingClassifications = value
}

/**
 * Set the flag that indicates that the app is loading the features.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingFeatures(state, value) {
  state.loadingFeatures = value
}

/**
 * Set the flag that indicates that the mapped items are being loaded.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingMappedItems(state, value) {
  state.loadingMappedItems = value
}

/**
 * Set the flag that indicates that a value is being remove from the app.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingRemoveValue(state, value) {
  state.loadingRemoveValue = value
}

/**
 * Set the flag that indicates that the app is saving the active map.
 * @param {Object} state
 * @param {Boolean} value
 */
function setLoadingSaveActiveMap(state, value) {
  state.loadingSaveActiveMap = value
}

/**
 * Set the array of objects with the mapped values.
 * @param {Object} state
 * @param {Object[]} values 
 */
function setMappedItems(state, values) {
  state.mappedItems = values
}

/**
 * Set the flag that indicates to show the mapped values instead of the raw values.
 * @param {Object} state
 * @param {Boolean} value
 */
function setShowingMappedValues(state, value) {
  state.showingMappedValues = value
  setActiveMappedItem(state, null)
}

/**
 * Toggle the sidebar visibility.
 * @param {Object} state
 */
function toggleSidebar(state) {
  state.showingSidebar = !state.showingSidebar
}

export default {
  setActiveMappedItem,
  setActiveMappedItemInput,
  setBrand,
  setClassification,
  setClassifications,
  setFeature,
  setFeatures,
  setLoadingClassifications,
  setLoadingFeatures,
  setLoadingMappedItems,
  setLoadingRemoveValue,
  setLoadingSaveActiveMap,
  setMappedItems,
  setShowingMappedValues,
  toggleSidebar
}
